import {useState, useEffect, ReactNode} from 'react';
import {observer} from 'mobx-react-lite';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import CustomerPortalAPI from '../APIs/CustomerPortalAPI';
import {CustomerPortalData} from './types';

const useStyles = makeStyles({
    table: {
        minWidth: 650,
    },
    nestedTable: {
        minWidth: 100,
    },
});

export const CustomerPortalDataTable = observer(() => {
    const [customerPortalData, setCustomerPortalData] = useState<CustomerPortalData>();
    const loadCustomerPortalData = async () => {
        try {
            const email = new URLSearchParams(window.location.search).get('email');
            const data = await CustomerPortalAPI.GetCustomerPortalData(email);
            setCustomerPortalData(data?.customerByEmail);
        } catch (error) {
            console.log(error);
        }
    };
    useEffect(() => {
        loadCustomerPortalData();
    }, []);

    const classes = useStyles();
    const objectToDisplay = (json: unknown, nested: boolean): ReactNode => {
        // `typeof null === 'object'`, so null/undefined must be handled before any Object.keys call.
        if (json === null || typeof json === 'undefined') {
            return null;
        }

        if (nested) {
            if (typeof json === 'boolean' || typeof json === 'string' || typeof json === 'number') {
                return <b>{JSON.stringify(json)}</b>;
            }

            if (Array.isArray(json)) {
                return (
                    <TableContainer component={Paper} style={{width: 'fit-content'}}>
                        <Table className={classes.nestedTable} size="small" style={{width: 'auto'}}>
                            <TableBody>
                                {json.map((obj: unknown, i: number) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <TableRow key={`item-${i}`}>
                                        <TableCell component="th" scope="row">
                                            {i + 1}
                                        </TableCell>
                                        <TableCell align="right">{objectToDisplay(obj, true)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            }

            if (typeof json === 'object') {
                return (
                    <TableContainer component={Paper} style={{width: 'fit-content'}}>
                        <Table className={classes.nestedTable} size="small" style={{width: 'auto'}}>
                            <TableBody>
                                {Object.entries(json as Record<string, unknown>).map(([nestedKey, nestedValue]) => (
                                    <TableRow key={nestedKey}>
                                        <TableCell component="th" scope="row">
                                            {nestedKey}
                                        </TableCell>
                                        <TableCell align="right">{objectToDisplay(nestedValue, true)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            }

            return null;
        }

        if (typeof json === 'object') {
            return Object.entries(json as Record<string, unknown>).map(([key, value]) => (
                <TableRow key={key}>
                    <TableCell component="th" scope="row">
                        {key}
                    </TableCell>
                    <TableCell align="right">{objectToDisplay(value, true)}</TableCell>
                </TableRow>
            ));
        }

        // Top-level primitive: still show the value rather than an error message.
        return (
            <TableRow>
                <TableCell component="th" scope="row" />
                <TableCell align="right">{objectToDisplay(json, true)}</TableCell>
            </TableRow>
        );
    };

    const isBlank = (value: unknown) => value === null || typeof value === 'undefined' || value === '';

    // Some records store the payload already parsed rather than as a JSON string, and some store
    // a plain string that isn't JSON at all — neither should blow up the page.
    const parseJsonValue = (jsonString: unknown): {parsed: true; value: unknown} | {parsed: false; raw: string} => {
        if (typeof jsonString !== 'string') {
            return {parsed: true, value: jsonString};
        }

        try {
            return {parsed: true, value: JSON.parse(jsonString)};
        } catch (_error) {
            return {parsed: false, raw: jsonString};
        }
    };

    // Emits TableRows — only valid directly inside a <TableBody>.
    const jsonToRows = (jsonString: unknown): ReactNode => {
        if (isBlank(jsonString)) {
            return null;
        }

        const result = parseJsonValue(jsonString);
        if (!result.parsed) {
            return (
                <TableRow>
                    <TableCell component="th" scope="row" />
                    <TableCell align="right">{result.raw}</TableCell>
                </TableRow>
            );
        }

        return objectToDisplay(result.value, false);
    };

    // Emits cell content (a nested table or a primitive) — safe inside a <TableCell>.
    const jsonToContent = (jsonString: unknown): ReactNode => {
        if (isBlank(jsonString)) {
            return null;
        }

        const result = parseJsonValue(jsonString);
        return result.parsed ? objectToDisplay(result.value, true) : result.raw;
    };

    return (
        <div style={{width: '100%'}}>
            <h1>Data From Customer Portal</h1>
            <TableContainer component={Paper}>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Value</TableCell>
                        </TableRow>
                    </TableHead>
                    {/* eslint-disable-next-line no-extra-boolean-cast */}
                    {!!customerPortalData ? (
                        <TableBody>
                            <TableRow key="firstName">
                                <TableCell component="th" scope="row">
                                    First name
                                </TableCell>
                                <TableCell>{customerPortalData?.firstName}</TableCell>
                            </TableRow>
                            <TableRow key="lastName">
                                <TableCell component="th" scope="row">
                                    Last name
                                </TableCell>
                                <TableCell>{customerPortalData?.lastName}</TableCell>
                            </TableRow>
                            <TableRow key="dob">
                                <TableCell component="th" scope="row">
                                    Date of Birth (YYYY-MM-DD)
                                </TableCell>
                                <TableCell>{customerPortalData?.dateOfBirth}</TableCell>
                            </TableRow>
                            <TableRow key="email">
                                <TableCell component="th" scope="row">
                                    Email
                                </TableCell>
                                <TableCell>{customerPortalData?.email}</TableCell>
                            </TableRow>
                            <TableRow key="consented">
                                <TableCell component="th" scope="row">
                                    Consented (UTC)
                                </TableCell>
                                <TableCell>{customerPortalData?.consent}</TableCell>
                            </TableRow>
                            {customerPortalData?.events?.map((event: any, i: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={`event${i}`}>
                                    <TableCell component="th" scope="row">
                                        Event {i + 1} (UTC)
                                    </TableCell>
                                    <TableCell>
                                        <b>{event.type}</b> at {event.date}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {customerPortalData?.identifications?.map((id: any, i: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={`id${i}`}>
                                    <TableCell component="th" scope="row">
                                        Identification {i + 1}
                                    </TableCell>
                                    <TableCell>
                                        <b>{id.type}</b>
                                        <br />
                                        {jsonToContent(id.value)}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {customerPortalData?.paymentInformations?.map((paymentInfo, i: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={`id${i}`}>
                                    <TableCell component="th" scope="row">
                                        Payment Info {i + 1}
                                    </TableCell>
                                    <TableCell>
                                        <b>Account Number</b>: {paymentInfo.accountNumber}
                                        <br />
                                        <b>Routing Number</b>: {paymentInfo.routingNumber}
                                        <br />
                                        <b>Payment Frequency</b>: {paymentInfo.paymentFrequency}
                                        <br />
                                        <b>Payment Day</b>: {paymentInfo.dayOfMonth}
                                        <br />
                                        <b>Description</b>: {paymentInfo.label}
                                        <br />
                                    </TableCell>
                                </TableRow>
                            ))}

                            {customerPortalData?.paperworks?.map((paperwork: any, it: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={`paperwork-${it}`}>
                                    <TableCell component="th" scope="row">
                                        Paperwork {it + 1}
                                    </TableCell>
                                    <TableCell>
                                        <TableContainer component={Paper} style={{width: 'fit-content'}}>
                                            <Table className={classes.nestedTable} size="small" style={{width: 'auto'}}>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            Type
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {paperwork?.form_spec?.label}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            Status
                                                        </TableCell>
                                                        <TableCell align="right">{paperwork?.status}</TableCell>
                                                    </TableRow>
                                                    {jsonToRows(paperwork?.completed_paperwork)}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {/* <b>Type: {paperwork?.form_spec?.label}</b>
                                        <br />
                                        <b>Status :{paperwork?.status}</b>
                                        <br />
                                        {jsonToContent(paperwork.completed_paperwork)} */}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {customerPortalData?.requirements?.map((requirement: any, it: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={`requirement-${it}`}>
                                    <TableCell component="th" scope="row">
                                        Requirement {it + 1}
                                    </TableCell>
                                    <TableCell>
                                        <b>Type: {requirement?.type}</b>
                                        <br />
                                        <b>Status :{requirement?.status}</b>
                                        <br />
                                        <b>data :{requirement?.data}</b>
                                        <br />

                                        {jsonToContent(requirement.data_json)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                <TableCell>No data available</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        </div>
    );
});
